import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/onboardingWizardCopy";
const I18n = I18nUtil.createScoped(Translations);
/** Onboarding wizard copy — ~...~ markers for i18n-google scan. */

/** Figma 97267:68495 — Value proposition copy */
export const VALUE_PROP_COPY = {
  headlineLine1: I18n.t("I'll read"),
  headlineLine2: I18n.t("your money"),
  headlineAccent: I18n.t("in 90 seconds."),
  steps: [
    {
      title: I18n.t("You answer a few quick questions"),
      body: I18n.t("Age, income, the basics — about a minute."),
    },
    {
      title: I18n.t("I read your money in the background"),
      body: I18n.t("I crunch the numbers while you wait."),
    },
    {
      title: I18n.t("You get your Fitness Score"),
      body: I18n.t("Your starting number — and a goal to lift it."),
    },
  ],
  cta: I18n.t("Let's go"),
} as const;

export const QUIZ_COMMON_COPY = {
  edIntro: I18n.t("I'm Ed — your money person."),
  continue: I18n.t("Continue"),
  greetingTemplate: (name: string) =>
    I18n.t("Good to meet you, %{var0}. Just a little about you first.", {
      var0: name,
    }),
} as const;

export const LOADER_COPY = {
  title: I18n.t("Reading your money"),
  syncingAccounts: I18n.t("Syncing your account data…"),
  syncProgress: (synced: number, total: number) =>
    I18n.t("Syncing your accounts (%{synced}/%{total})", {
      synced: String(synced),
      total: String(total),
    }),
  errorTitle: I18n.t("Hmm — I hit a snag."),
  errorHint: I18n.t("Your answers are safe — nothing's lost."),
  retry: I18n.t("Try again"),
  syncAnomalyTitle: I18n.t("Data sync issue"),
  syncAnomalyCopy: I18n.t(
    "Some account data didn't finish syncing. Please try again in a little while — refresh to restart.",
  ),
  syncFailed: (count: number) =>
    I18n.t("%{count} accounts failed to sync. Please try again later.", {
      count: String(count),
    }),
  /**
   * @deprecated checklist 一律走 `resolveLoaderChecklist()`
   * （`@hooks/onboarding/loaderChecklistByGoalType`）。这四行与
   * `getLoaderChecklistForGoalType("million")` 逐行字面相等，保留仅为词表锚点
   * （删掉会让 i18n-google 扫描丢失这四条 key）。**不要在组件里引用它。**
   */
  rows: [
    I18n.t("Reading your income & savings"),
    I18n.t("Compounding your investments"),
    I18n.t("Finding people on your path"),
    I18n.t("Pinpointing your $1M year"),
  ],
} as const;

export const RISK_PROFILE_LOADING_COPY = {
  message: (name: string) =>
    I18n.t("Got it, %{var0}. Give me a moment to put your profile together.", {
      var0: name,
    }),
  errorTitle: I18n.t("I couldn't finish that one."),
  errorBody: I18n.t("Your answers are saved, so this is just one more tap."),
  retry: I18n.t("Try again"),
} as const;

export const INSIGHT_COPY = {
  aboutPrefix: I18n.t("Insights about"),
  fromEd: I18n.t("From Ed"),
  continue: I18n.t("Continue"),
} as const;

export const GOAL_SELECTION_COPY = {
  title: I18n.t("Let's fix one thing first"),
  subtitle: I18n.t(
    "Pick the one you want me to take on now. The rest stay on your list — we'll get to them together.",
  ),
  rank1Chip: I18n.t("Start here — the biggest gain for you"),
  needsAccountHint: I18n.t("Needs your investment account"),
  /** 验收 P1-4：极端画像过滤后卡池为空的兜底提示（继续按钮禁用） */
  emptyHint: I18n.t(
    "Nothing stands out as broken — go back and adjust your answers, or keep chatting with Ed.",
  ),
  /** PRD S3.5：退回选题页 toast（无按钮） */
  bounceNoHoldingsToast: I18n.t(
    "Ed needs to see your holdings for this one — link, upload, or start somewhere else.",
  ),
  continue: I18n.t("Continue"),
} as const;

export const PLAN_COPY = {
  goalSectionTitle: I18n.t("Goal for you"),
  planSectionTitle: I18n.t("Your Plan"),
  planSectionSubtitle: I18n.t("5 moves get you there sooner"),
  unlockCta: I18n.t("Unlock my full plan"),
  sampleGoal: I18n.t(
    "Bring single-name concentration into your 15% guardrail, staged over 6 months.",
  ),
} as const;

/**
 * UI12 Result – AI impact 的**固定文案**（Figma App `108867:131447` / `108867:131605`）。
 *
 * ⚠️ 这里只放「结构性标题」——**正文一律来自 `generate` 响应**
 * （`aiImpact.whyThisNumber` / `handoffLead` / `handoffBody` / `coverage` / `peers`）。
 *
 * ⚠️ **只维护一套**：新稿 App 与 PC 逐字不同（M0 `line of work` vs `industry`、
 * M1 `Tasks already done with AI` vs `The part of your work AI can already do`、
 * M3 `What this means` vs `Why this number`）。fe-spec §4.6 UI12 约束 ① 明令
 * **不为任一端硬编码两套**，此处取 App 稿逐字，差异交 PM 收敛（§7-R16）。
 *
 * ⚠️ `coverageCaption` 是 **R16 的唯一例外**：2026-09-21 PM 指名改回 Figma
 * `108867:131468` 的逐字文案，PRD 口径
 * `Measured across everything the job involves day to day, task by task.` 作废。
 * 其余文案仍按 R16 取 PRD。
 */
export const AI_IMPACT_RESULT_COPY = {
  heroTitle: I18n.t("The shift in your line of work has already started."),
  heroSubtitle: I18n.t("Same job, new way of doing it."),
  coverageTitle: I18n.t("Tasks already done with AI"),
  coverageCaption: I18n.t(
    "This much is already being done with AI by people in your role.",
  ),
  peerYouChip: I18n.t("You"),
  peerCollapsedTitle: (categoryLabel: string) =>
    I18n.t("Roles closest to yours in %{var0}", { var0: categoryLabel }),
  peerExpandedTitle: (categoryLabel: string) =>
    I18n.t("Across %{var0}", { var0: categoryLabel }),
  peerExpandCta: (total: number) =>
    I18n.t("Show all %{var0} roles", { var0: total }),
  peerCollapseCta: I18n.t("Show fewer"),
  whyThisNumberTitle: I18n.t("What this means"),
} as const;

/**
 * UI0 的文案套别。
 *
 * - `default` —— 现网 fitness score 版（Figma `102803:53488`）
 * - `ai-impact` —— `ai_job_risk` 广告场景版（Figma App `108867:131558` / PC `108867:182218`）
 *
 * 两套 **key 必须同名**，`ScoreIntroStep` 才能一套渲染逻辑吃两份数据。
 */
export type ScoreIntroVariant = "default" | "ai-impact";

export interface ScoreIntroLegendRow {
  title: string;
  body: string;
  /** 色点的 UnoCSS 语义 class（`content/brand/*` 三档透明度） */
  dotClass: string;
}

export interface ScoreIntroCopy {
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  cta: string;
  scan: string;
  /** 环形图顶轴标 */
  axisTop: string;
  /** 环形图左下轴标 */
  axisLeft: string;
  /** 环形图右下轴标 */
  axisRight: string;
  legend: readonly ScoreIntroLegendRow[];
}

/**
 * 默认场景 —— 逐字即现网，是 AC1 的回归基线，**一个字都不许改**。
 *
 * 轴 key 由 `axisControl` / `axisSafety` / `axisGrowth` 改名为位置化的
 * `axisTop` / `axisLeft` / `axisRight`（纯改名：渲染位置与文案逐字不变）。
 */
const SCORE_INTRO_COPY_DEFAULT = {
  titleLine1: I18n.t("Meet your"),
  titleLine2: I18n.t("financial fitness score"),
  subtitle: I18n.t("One number for how your money's really doing."),
  cta: I18n.t("Get my score"),
  scan: I18n.t("A 90-second money scan"),
  axisTop: I18n.t("Control"),
  axisLeft: I18n.t("Safety"),
  axisRight: I18n.t("Growth"),
  legend: [
    {
      title: I18n.t("Control"),
      body: I18n.t("Your monthly cash flow"),
      dotClass: "bg-content_brand_primary",
    },
    {
      title: I18n.t("Safety"),
      body: I18n.t("Your cushion if a shock hits"),
      dotClass: "bg-content_brand_tertiary",
    },
    {
      title: I18n.t("Growth"),
      body: I18n.t("Your money building"),
      dotClass: "bg-content_brand_quaternary",
    },
  ],
} as const satisfies ScoreIntroCopy;

/**
 * `ai_job_risk` 场景 —— 文案取 PRD（slice-4-impl.md 逐帧 Spec 表），**不取 Figma**。
 *
 * ⚠️ 左下轴是 `Your line`：App 帧 `108867:131575` 画的是 `Your peers`，按产品裁定 R16
 * 「一律以 PRD 为准」**不采纳**；PC 帧 `108867:182234` 本身画的就是 `Your line`。
 */
const SCORE_INTRO_COPY_AI_IMPACT = {
  titleLine1: I18n.t("See how much of your work"),
  titleLine2: I18n.t("AI already does"),
  subtitle: I18n.t("Then a money plan sized to your numbers."),
  cta: I18n.t("Get my number"),
  scan: I18n.t("A 90-second scan · 9 quick questions"),
  axisTop: I18n.t("Your role"),
  axisLeft: I18n.t("Your line"),
  axisRight: I18n.t("Your money"),
  legend: [
    {
      title: I18n.t("AI coverage of your role"),
      body: I18n.t("How much of the day-to-day is already done with AI"),
      dotClass: "bg-content_brand_primary",
    },
    {
      title: I18n.t("Where you sit"),
      body: I18n.t("Against roles like yours in the same line"),
      dotClass: "bg-content_brand_tertiary",
    },
    {
      title: I18n.t("A plan for your money"),
      body: I18n.t("One goal you can start today"),
      dotClass: "bg-content_brand_quaternary",
    },
  ],
} as const satisfies ScoreIntroCopy;

/**
 * 具名导出保留并指向默认套 —— 存量引用点（如 `fitness/FitnessScoreLegendCard`）零改动。
 * **新代码一律走 {@link getScoreIntroCopy}。**
 */
export const SCORE_INTRO_COPY = SCORE_INTRO_COPY_DEFAULT;

export const getScoreIntroCopy = (
  variant: ScoreIntroVariant,
): ScoreIntroCopy =>
  variant === "ai-impact"
    ? SCORE_INTRO_COPY_AI_IMPACT
    : SCORE_INTRO_COPY_DEFAULT;

export const FITNESS_SCORE_RESULT_COPY = {
  radarTitle: I18n.t("Your Financial fitness score"),
  cardTitle: I18n.t("What's in your score"),
  cta: I18n.t("See where you're headed"),
  dimensions: {
    control: I18n.t("Control"),
    safety: I18n.t("Safety"),
    growth: I18n.t("Growth"),
  },
} as const;
